'use client';

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { KBareButton, KButton } from './button';
import { KDialog } from './dialog';
import { cx } from './cx';

/** Одна колонка барабана: пункты по порядку и текущее значение. */
export interface KWheelColumn {
  key: string;
  items: { value: string; label: ReactNode }[];
  value: string;
  /** Подпись единицы справа от колонки (мин / ч / дн) — у барабана «число · единица». */
  unit?: ReactNode;
  /** По кругу (часы, минуты): за последней строкой снова первая, как у колеса iOS. */
  cyclic?: boolean;
}

/** ГЕОМЕТРИЯ БАРАБАНА ПО ВИДУ ОКНА (ТЗ WEB-CAUSES О9; исходные строки TODO web-fix-1003 П7/П13/П14).
 *  Числа — с кадров iOS, у двух видов они РАЗНЫЕ (UIPickerView в алерте и в листе):
 *  — окно по центру (K04-notif-quiet-time-pick, R04-broadcast-time-picker): шаг строки у центра 32
 *    (пилюля 33 pt), видимая высота ~179 (5,6 строки) — прежние числа, кадру соответствуют;
 *  — ЛИСТ снизу (W56-chain-msg-when): строка 22 (пилюля 24 pt), белая карточка барабана 165 pt
 *    = 6,4 строки × 22 + поля карточки 12 + 12. Было 32 и карточка 255 (TODO П7 «22 против 32»).
 *  Шаг строки — та же величина в CSS `--k-wheel-row` и в расчёте прокрутки. */
const GEOM = {
  center: { row: 32, visible: 5.6 },
  sheet: { row: 22, visible: 6.4 },
} as const;
/** Угол поворота «цилиндра» UIPickerView на одну строку (рад). Снят с кадра R04: строки от
 *  центра на 31,4 · 58,5 · 77,7 pt при шаге 32 → 2·cos θ = 1,863, θ = 0,3735; радиус барабана
 *  r = шаг / θ ≈ 2,68 шага. Строка на удалении d рисуется 2D-сдвигом к центру и сжатием по
 *  высоте cos(dθ) — дальние строки сжаты, как у родного колеса (без 3D-перспективы). */
const THETA = 0.3735;

/**
 * БАРАБАН ВЫБОРА — ОДИН НА КАБИНЕТ (ТЗ WEB-PARITY-3, этап 3; владелец 02.10.2026: «свой
 * барабан-пикер, один на всё»). Окно по центру как системный UIPickerView в алерте iOS
 * (кадры K04-notif-quiet-time-pick, R04-broadcast-time-picker, W56-chain-msg-when):
 * заголовок, колонки с прокруткой и привязкой к строке, серая пилюля за выбранной
 * строкой, под барабаном серая капсула «Готово». Колонок сколько угодно: часы : минуты,
 * число · единица, день · месяц · год. Значение применяется ТОЛЬКО по «Готово»; закрытие
 * мимо и Escape — отмена (как у системного окна).
 *
 * Прокрутка — родная (`scroll-snap`), выбранное читается по положению прокрутки; тап
 * по строке докручивает к ней. В iOS-приложении барабан рисует натив (`pickTime`), этот
 * компонент там не живёт — его зовут только веб-поля.
 */
export function KWheelDialog({
  open,
  title,
  columns,
  doneLabel,
  onDone,
  onClose,
  width,
  sheet = false,
  closeLabel,
  head,
}: {
  open: boolean;
  title: ReactNode;
  columns: KWheelColumn[];
  doneLabel: ReactNode;
  /** Значения колонок по порядку — по «Готово». */
  onDone: (values: string[]) => void;
  onClose: () => void;
  width?: number;
  /** ЛИСТОМ снизу (телефон в браузере; кадр iOS W56-chain-msg-when): шапка «× · заголовок»,
   *  барабан белой карточкой, розовая «Готово» внизу. Окно по центру — как было (K04/R04). */
  sheet?: boolean;
  /** Подпись кружка «×» листа (доступность). */
  closeLabel?: string;
  /** Над барабаном — свой ряд (сегмент единиц паузы, QUICK-START §11.16 п.3). */
  head?: ReactNode;
}) {
  const geom = sheet ? GEOM.sheet : GEOM.center;
  const picked = useRef<string[]>(columns.map((c) => c.value));
  useEffect(() => {
    if (open) picked.current = columns.map((c) => c.value);
  }, [open, columns]);
  return (
    <KDialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
      title={
        sheet ? (
          <span className="k-wheel-sheet-head">
            <KBareButton
              className="k-wheel-sheet-x"
              aria-label={closeLabel ?? '×'}
              onClick={onClose}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </KBareButton>
            <span>{title}</span>
          </span>
        ) : (
          title
        )
      }
      className={cx('k-dlg--wheel', sheet && 'k-dlg--wheel-sheet')}
      width={width}
      footer={
        <KButton
          {...(sheet ? { tone: 'primary' as const } : {})}
          onClick={() => onDone([...picked.current])}
        >
          {doneLabel}
        </KButton>
      }
    >
      {head ? <div className="k-wheel-head">{head}</div> : null}
      <div
        className="k-wheel"
        style={{ ['--k-wheel-row' as string]: `${geom.row}px`, ['--k-wheel-n' as string]: geom.visible }}
      >
        <div className="k-wheel__pill" aria-hidden />
        {columns.map((c, i) => (
          <WheelColumn
            key={c.key}
            column={c}
            row={geom.row}
            visible={geom.visible}
            onChange={(v) => {
              picked.current = picked.current.map((x, j) => (j === i ? v : x));
            }}
          />
        ))}
      </div>
    </KDialog>
  );
}

function WheelColumn({
  column,
  row: ROW,
  visible: VISIBLE,
  onChange,
}: {
  column: KWheelColumn;
  row: number;
  visible: number;
  onChange: (v: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const n = column.items.length;
  // По кругу — три копии списка подряд, стоим в средней; подъехали к краю — прыгаем на
  // копию без анимации (глазу незаметно: строки те же).
  const cyclic = !!column.cyclic && n > VISIBLE;
  const rows = cyclic ? [...column.items, ...column.items, ...column.items] : column.items;
  const base = cyclic ? n : 0;
  const start = Math.max(
    0,
    column.items.findIndex((it) => it.value === column.value),
  );
  const [cur, setCur] = useState(start);
  const pad = ((VISIBLE - 1) / 2) * ROW;

  // ЦИЛИНДР: каждая строка у края сдвигается к центру и сжимается по высоте (см. THETA). Пишем
  // transform/opacity прямо в узлы — без перерисовки React на каждый кадр прокрутки; трогаем
  // только строки в пределах видимого окна ±1.
  const bend = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const r = ROW / THETA;
    const mid = el.scrollTop + el.clientHeight / 2;
    const kids = el.children;
    const first = Math.max(0, Math.floor((el.scrollTop - pad) / ROW) - 1);
    const last = Math.min(kids.length - 1, Math.ceil((el.scrollTop + el.clientHeight - pad) / ROW) + 1);
    for (let k = Math.max(0, first - 3); k <= Math.min(kids.length - 1, last + 3); k++) {
      const node = kids[k] as HTMLElement;
      // Гнём ПОДПИСЬ, а не саму строку: строка — точка привязки прокрутки (scroll-snap), и
      // сдвиг её рамки браузер принимает за смену точек привязки — колесо «доезжало» лишние
      // строки (проба 06.10: +3 строки давали +5).
      const lab = (node.firstElementChild as HTMLElement | null) ?? node;
      if (k < first || k > last) {
        lab.style.transform = '';
        node.style.opacity = '';
        continue;
      }
      const d = (pad + k * ROW + ROW / 2 - mid) / ROW;
      const a = Math.max(-Math.PI / 2, Math.min(Math.PI / 2, d * THETA));
      const dy = r * Math.sin(a) - d * ROW;
      const sy = Math.max(0, Math.cos(a));
      lab.style.transform = `translateY(${dy.toFixed(2)}px) scaleY(${sy.toFixed(3)})`;
      node.style.opacity = sy < 0.2 ? '0' : '';
    }
  }, [ROW, pad]);

  // Начальное положение — выбранная строка по центру, без анимации; сразу же изгиб цилиндра
  // (до первого кадра, иначе строки мелькнут ровными).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.scrollTop = (base + start) * ROW;
    bend();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- только при открытии
  }, []);

  const onScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    bend();
    let idx = Math.min(rows.length - 1, Math.max(0, Math.round(el.scrollTop / ROW)));
    if (cyclic) {
      // Ушли в крайнюю копию — переносимся на ту же строку средней.
      if (idx < n / 2) {
        el.scrollTop += n * ROW;
        idx += n;
      } else if (idx >= n * 2.5) {
        el.scrollTop -= n * ROW;
        idx -= n;
      }
    }
    const i = idx % n;
    if (i !== cur) {
      setCur(i);
      onChange(column.items[i]!.value);
    }
  }, [ROW, bend, column.items, cur, cyclic, n, onChange, rows.length]);

  const goTo = (rowIndex: number) => {
    ref.current?.scrollTo({ top: rowIndex * ROW, behavior: 'smooth' });
  };

  return (
    <div className="k-wheel__col">
      <div className="k-wheel__scroll" ref={ref} onScroll={onScroll} style={{ paddingBlock: pad }}>
        {rows.map((it, r) => (
          <button
            key={`${r}-${it.value}`}
            type="button"
            className={cx('k-wheel__item', r % n === cur && 'is-on')}
            onClick={() => goTo(r)}
            tabIndex={-1}
          >
            <span className="k-wheel__lab">{it.label}</span>
          </button>
        ))}
      </div>
      {column.unit ? <span className="k-wheel__unit">{column.unit}</span> : null}
    </div>
  );
}
